import { normalizeSearch } from '../data/store';
import type {
  CalendarDay,
  DimensionFilters,
  Flow,
  FlowQuery,
  SearchableTransaction,
  SeriesResult,
  Transaction,
} from '../data/types';
import { assertMovingAverage, inSet, trailingAverage } from './shared';
import { measured } from '../debug/telemetry';

type Grain = FlowQuery['grain'];

function bucketFor(item: CalendarDay | Transaction, grain: Grain): string {
  switch (grain) {
    case 'day':
      return item.date;
    case 'week':
      return item.week;
    case 'month':
      return item.month;
    case 'quarter':
      return item.quarter;
    case 'year':
      return String(item.year);
  }
}

function categoryMatches(
  ids: ReadonlyArray<string | null>,
  selected: ReadonlySet<string> | undefined,
  includeUncategorized: boolean,
): boolean {
  if (selected === undefined) return true;
  const availableIds = ids.filter((id): id is string => id !== null);
  return availableIds.length === 0
    ? includeUncategorized
    : availableIds.some((id) => selected.has(id));
}

export function matchesDimensionFilters(
  transaction: SearchableTransaction,
  filters: DimensionFilters,
): boolean {
  if (!inSet(transaction.account_id, filters.accountIds)) return false;
  if (!inSet(transaction.account_group_id, filters.accountGroupIds)) return false;
  if (
    !categoryMatches(
      [transaction.category_id, transaction.parent_category_id, transaction.rollup_category_id],
      filters.categoryIds,
      filters.includeUncategorized === true,
    )
  ) {
    return false;
  }
  if (
    !categoryMatches(
      [transaction.rollup_category_id],
      filters.rollupCategoryIds,
      filters.includeUncategorized === true,
    )
  ) {
    return false;
  }
  const text = normalizedQuery(filters.text);
  return !text || transaction.descriptionSearch.includes(text);
}

// La stessa query testuale viene confrontata con migliaia di transazioni per ogni ricalcolo:
// normalizzarla una sola volta evita NFD + regex per riga.
let lastRawQuery: string | undefined;
let lastNormalizedQuery = '';
function normalizedQuery(raw: string | undefined): string {
  if (!raw) return '';
  if (raw !== lastRawQuery) {
    lastRawQuery = raw;
    lastNormalizedQuery = normalizeSearch(raw);
  }
  return lastNormalizedQuery;
}

function flowIsIncluded(flow: Flow, query: FlowQuery): boolean {
  const selected = query.filters.flows;
  if (selected !== undefined && !selected.has(flow)) return false;

  switch (query.metric) {
    case 'income':
      return flow === 'income';
    case 'expense':
      return flow === 'expense';
    case 'net':
      return flow === 'income' || flow === 'expense';
    case 'volume':
    case 'count':
      return selected ? selected.has(flow) : flow !== 'transfer';
  }
}

function metricValue(transaction: SearchableTransaction, query: FlowQuery): number {
  switch (query.metric) {
    case 'income':
      return transaction.amount_cents;
    case 'expense':
      return Math.abs(transaction.amount_cents);
    case 'net':
      return transaction.amount_cents;
    case 'volume':
      return Math.abs(transaction.amount_cents);
    case 'count':
      return 1;
  }
}

function breakdownId(transaction: SearchableTransaction, query: FlowQuery): string {
  switch (query.breakdown) {
    case 'none':
      return 'total';
    case 'account':
      return transaction.account_id;
    case 'group':
      return transaction.account_group_id;
    case 'category':
      return transaction.category_id ?? '__uncategorized__';
    case 'rollup-category':
      return transaction.rollup_category_id ?? '__uncategorized__';
  }
}

function queryFlowImpl(
  transactions: readonly SearchableTransaction[],
  calendar: readonly CalendarDay[],
  query: FlowQuery,
): SeriesResult {
  assertMovingAverage(query.movingAverage);
  if (query.visiblePeriod.from > query.visiblePeriod.to) {
    throw new RangeError('Il periodo visibile è invertito.');
  }

  const calendarUntilEnd = calendar.filter((day) => day.date <= query.visiblePeriod.to);
  const allBuckets = Array.from(
    new Set(calendarUntilEnd.map((day) => bucketFor(day, query.grain))),
  );
  const visibleDays = calendar.filter(
    (day) => day.date >= query.visiblePeriod.from && day.date <= query.visiblePeriod.to,
  );
  const visibleBuckets = Array.from(new Set(visibleDays.map((day) => bucketFor(day, query.grain))));

  if (visibleBuckets.length === 0) {
    return {
      buckets: [],
      series: [],
      total: 0,
      unit: query.metric === 'count' ? 'count' : 'cents',
    };
  }

  const firstVisible = allBuckets.indexOf(visibleBuckets[0] ?? '');
  const lookback = (query.movingAverage?.window ?? 1) - 1;
  const extendedBuckets = allBuckets.slice(Math.max(0, firstVisible - lookback));
  const bucketIndex = new Map(extendedBuckets.map((bucket, index) => [bucket, index]));
  const valuesBySeries = new Map<string, number[]>();
  let visibleTotal = 0;

  for (const transaction of transactions) {
    if (transaction.date > query.visiblePeriod.to) continue;
    if (!matchesDimensionFilters(transaction, query.filters)) continue;
    if (!flowIsIncluded(transaction.flow, query)) continue;

    const bucket = bucketFor(transaction, query.grain);
    const index = bucketIndex.get(bucket);
    if (index === undefined) continue;

    const id = breakdownId(transaction, query);
    const values = valuesBySeries.get(id) ?? Array<number>(extendedBuckets.length).fill(0);
    values[index] = (values[index] ?? 0) + metricValue(transaction, query);
    valuesBySeries.set(id, values);

    if (
      transaction.date >= query.visiblePeriod.from &&
      transaction.date <= query.visiblePeriod.to
    ) {
      visibleTotal += metricValue(transaction, query);
    }
  }

  if (query.breakdown === 'none' && !valuesBySeries.has('total')) {
    valuesBySeries.set('total', Array<number>(extendedBuckets.length).fill(0));
  }

  const visibleStart = extendedBuckets.indexOf(visibleBuckets[0] ?? '');
  const outputSeries = Array.from(valuesBySeries, ([id, values]) => {
    const calculated = query.movingAverage
      ? trailingAverage(values, query.movingAverage.window)
      : values;
    return {
      id,
      label: id,
      values: calculated.slice(visibleStart),
    };
  }).sort((left, right) => left.id.localeCompare(right.id));

  return {
    buckets: extendedBuckets.slice(visibleStart),
    series: outputSeries,
    total: visibleTotal,
    unit: query.metric === 'count' ? 'count' : 'cents',
  };
}

export const queryFlow = measured('analytics.flow', queryFlowImpl);
